import { useEffect, useRef } from 'react';
import { useStore } from 'react-redux';

import { hasArrowKeyOwner } from '../utils/arrowKeyOwnership';

/**
 * ArrowLeft/ArrowRight step the calendar exactly as the header's nav arrows do —
 * a day in day view, a week in week view, a month in month view. It hands off to
 * the header's own `addDay`/`subtractDay`, so the pre-save discard guard and the
 * per-view branching stay in one place.
 *
 * @param {object} params
 * @param {() => void} params.onPrev — the header's subtractDay
 * @param {() => void} params.onNext — the header's addDay
 */
const useCalendarArrowNavigation = ({ onPrev, onNext }) => {
  const store = useStore();

  /** The header rebuilds these closures every render. Held in a ref so the
   * listener attaches once instead of tearing down on each re-render. */
  const handlersRef = useRef({ onPrev, onNext });
  handlersRef.current = { onPrev, onNext };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;

      /** A held arrow would otherwise fire a burst of date changes, each one a
       * refetch. One press, one day. */
      if (e.repeat) return;

      const tag = e.target.tagName;
      if ((tag === 'INPUT' && e.target.type !== 'checkbox')
        || tag === 'TEXTAREA'
        || e.target.isContentEditable) {
        return;
      }

      /** Left/right drive the open popup's own navigation — the options menu
       * opens submenus with ArrowRight, the pace and superset pickers close on
       * ArrowLeft. Bail without preventDefault so their handlers still see it. */
      if (hasArrowKeyOwner()) return;

      const { calendar } = store.getState();
      /** activeModal, not the modal handler's displayModal — that one lags by a
       * 250ms close animation. The date must not shift under an open modal. */
      if (calendar.data.activeModal || calendar.data.activeSecondLayerModal) return;
      if (calendar.data.dragIsInProgress) return;

      /** Claim the key so the calendar container doesn't also scroll sideways. */
      e.preventDefault();

      if (e.key === 'ArrowRight') {
        handlersRef.current.onNext();
      } else {
        handlersRef.current.onPrev();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [store]);
};

export default useCalendarArrowNavigation;
